// In-app community notifications client.
//
// Thin wrappers around the /api/notifications/* endpoints added for the
// community notifications feature (replies, likes, mentions, admin edits).
// Every call carries the Bearer token via getAuthHeaders so it is impossible
// to forget auth on a new call site (same rationale as lib/authHeaders.js).
//
// All helpers fail soft: on a network/auth error they resolve to a safe empty
// shape rather than throwing, so a transient backend hiccup never crashes the
// nav bell or the notifications page.
import axios from "axios";
import { getAuthHeaders, isAuthenticated } from "./authHeaders";

const API = `${""}/api`;

/** Lightweight unread count for the nav badge. Returns a number (0 on error). */
export async function fetchUnreadCount() {
  if (!isAuthenticated()) return 0;
  try {
    const res = await axios.get(`${API}/notifications/unread_count`, { headers: getAuthHeaders() });
    return Number(res.data?.unread_count) || 0;
  } catch {
    return 0;
  }
}

/**
 * Fetch a page of notifications.
 * opts: { unreadOnly?: boolean, limit?: number }
 * Returns { notifications: [], total: 0, unread_count: 0 }.
 */
export async function fetchList(opts = {}) {
  const empty = { notifications: [], total: 0, unread_count: 0 };
  if (!isAuthenticated()) return empty;
  try {
    const params = new URLSearchParams();
    if (opts.unreadOnly) params.set("unread_only", "true");
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const res = await axios.get(`${API}/notifications${qs ? `?${qs}` : ""}`, { headers: getAuthHeaders() });
    return {
      notifications: Array.isArray(res.data?.notifications) ? res.data.notifications : [],
      total: Number(res.data?.total) || 0,
      unread_count: Number(res.data?.unread_count) || 0,
    };
  } catch {
    return empty;
  }
}

/** Mark a single notification read. Returns true on success. */
export async function markRead(id) {
  try {
    await axios.post(`${API}/notifications/${encodeURIComponent(id)}/read`, {}, { headers: getAuthHeaders() });
    return true;
  } catch {
    return false;
  }
}

/** Mark all of the current user's notifications read. Returns true on success. */
export async function markAllRead() {
  try {
    await axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders() });
    return true;
  } catch {
    return false;
  }
}

/** Delete a single notification. Returns true on success. */
export async function del(id) {
  try {
    await axios.delete(`${API}/notifications/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });
    return true;
  } catch {
    return false;
  }
}

/** Compact relative-time formatter: "just now", "5m ago", "2h ago", "3d ago". */
export function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Build a one-line human message for a notification row.
 * Falls back gracefully when fields are missing on legacy documents.
 */
export function notificationMessage(n) {
  const who = n.actor_name || "Someone";
  const excerpt = n.post_excerpt ? `: "${n.post_excerpt}${n.post_excerpt.length >= 80 ? "…" : ""}"` : "";
  switch (n.type) {
    case "like":
      return `${who} liked your post${excerpt}`;
    case "reply":
      return `${who} replied to your post${excerpt}`;
    case "mention":
      return `${who} mentioned you${excerpt}`;
    case "post_edit_by_admin":
      return `${who} (admin) modified your post${excerpt}`;
    default:
      return `${who} interacted with your post${excerpt}`;
  }
}
